const literal = value => encodeURIComponent(String(value).replace(/'/g, "''")).replace(/'/g, '%27');
const rows = data => data?.value || data?.d?.results || [];

export function createCSOFeedbackClient(instance, account, fetchImpl = fetch, canChangeStatus = false) {
  const site = (process.env.REACT_APP_CSO_FEEDBACK_SITE_URL || '').replace(/\/$/, '');
  const list = process.env.REACT_APP_CSO_FEEDBACK_LIST_NAME;
  const base = `${site}/_api/web/lists/getbytitle('${literal(list || '')}')`;
  let fieldsPromise;
  let userPromise;
  const currentUser = () => {
    if (!userPromise) userPromise = call(`${site}/_api/web/currentuser?$select=Id`).catch(error => { userPromise = null; throw error; });
    return userPromise;
  };
  const isOwner = (item, user) => !!user?.Id && Number(item.AuthorId) === Number(user.Id);
  async function call(url, options = {}) {
    if (!site || !list) throw new Error('CSO Feedback SharePoint configuration is missing.');
    if (!account) throw new Error('Sign in to access CSO Feedback.');
    if (!url.startsWith(`${site}/_api/`)) throw new Error('Invalid SharePoint response URL.');
    // Match the delegated SharePoint resource used by QA Feedback.
    const token = await instance.acquireTokenSilent({ account, scopes: ['https://watertronics-my.sharepoint.com/.default'] });
    const response = await fetchImpl(url, { ...options, headers: {
      Authorization: `Bearer ${token.accessToken}`, Accept: 'application/json;odata=nometadata', ...options.headers,
    } });
    if (!response.ok) {
      const error = new Error(response.status === 412 ? 'This submission was changed by another user. Reload it before saving.' : `SharePoint returned ${response.status}. Check your access or try again.`);
      error.status = response.status;
      throw error;
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
  }
  async function all(url) {
    const result = [];
    const seen = new Set();
    while (url && !seen.has(url)) {
      seen.add(url);
      const data = await call(url);
      result.push(...rows(data));
      url = data['odata.nextLink'] || data['@odata.nextLink'] || data.d?.__next;
    }
    return result;
  }
  function fields() {
    if (!fieldsPromise) fieldsPromise = all(`${base}/fields?$select=Title,InternalName`).then(definitions => {
      const mapping = {};
      for (const name of ['Title', 'ProjectId', 'Notes', 'Status', 'SubmittedBy']) {
        const field = definitions.find(field => field.InternalName === name) || definitions.find(field => field.Title === name);
        if (!field) throw new Error(`The CSO Feedback list is missing ${name}.`);
        mapping[name] = field.InternalName;
      }
      return mapping;
    }).catch(error => { fieldsPromise = null; throw error; });
    return fieldsPromise;
  }
  const itemUrl = id => {
    if (!Number.isInteger(Number(id)) || Number(id) <= 0) throw new Error('Invalid submission ID.');
    return `${base}/items(${Number(id)})`;
  };
  return {
    async list(projectId) {
      if (!Number.isInteger(Number(projectId)) || Number(projectId) <= 0) throw new Error('Select a job first.');
      const mapping = await fields();
      const user = await currentUser();
      const items = await all(`${base}/items?$filter=${mapping.ProjectId}%20eq%20${Number(projectId)}&$orderby=Created%20desc&$expand=AttachmentFiles`);
      return items.map(item => ({ ...item, ...Object.fromEntries(Object.entries(mapping).map(([key, internal]) => [key, item[internal]])),
        canEditDetails: isOwner(item, user),
        AttachmentFiles: item.AttachmentFiles?.results || item.AttachmentFiles || [],
      }));
    },
    async save(projectId, values, existing) {
      const mapping = await fields();
      let data = { Title: values.Title.trim(), Notes: values.Notes, Status: 'Open' };
      if (existing) {
        const user = await currentUser();
        const item = await call(`${itemUrl(existing.Id)}?$select=AuthorId`);
        const owner = isOwner(item, user);
        if (!owner && !canChangeStatus) throw new Error('Only the submitter can edit this submission.');
        data = owner ? { Title: values.Title.trim(), Notes: values.Notes } : {};
        if (canChangeStatus) data.Status = values.Status.trim();
      }
      if (!existing) Object.assign(data, { ProjectId: Number(projectId), SubmittedBy: account.name || account.username });
      const body = JSON.stringify(Object.fromEntries(Object.entries(data).map(([key, value]) => [mapping[key], value])));
      try {
        const result = await call(existing ? itemUrl(existing.Id) : `${base}/items`, {
          method: 'POST', body, headers: { 'Content-Type': 'application/json;odata=nometadata',
            ...(existing ? { 'X-HTTP-Method': 'MERGE', 'IF-MATCH': existing['odata.etag'] || existing['@odata.etag'] || existing.__metadata?.etag || '*' } : {}),
          },
        });
        const id = existing?.Id || result?.Id || result?.d?.Id;
        if (!id) throw new Error('Missing saved submission ID.');
        return id;
      } catch (error) {
        if (!existing && (!error.status || error.status >= 500)) {
          error.uncertain = true;
          error.message = 'The save could not be confirmed. Close this form and reload submissions before trying again to avoid a duplicate.';
        }
        throw error;
      }
    },
    async attachments(id, files, removals, onUploaded, onRemoved) {
      if (!files.length && !removals.length) return;
      const user = await currentUser();
      const item = await call(`${itemUrl(id)}?$select=AuthorId`);
      if (!isOwner(item, user)) throw new Error('Only the submitter can change attachments.');
      const url = `${itemUrl(id)}/AttachmentFiles`;
      const existing = new Set((await all(url)).map(file => file.FileName.toLowerCase()));
      for (const name of removals) {
        if (existing.has(name.toLowerCase())) await call(`${url}/getByFileName('${literal(name)}')`, { method: 'POST', headers: { 'X-HTTP-Method': 'DELETE', 'IF-MATCH': '*' } });
        existing.delete(name.toLowerCase());
        onRemoved(name);
      }
      for (const file of files) {
        // A previous upload may have succeeded even if its response was interrupted.
        if (!existing.has(file.name.toLowerCase())) await call(`${url}/add(FileName='${literal(file.name)}')`, {
          method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: file,
        });
        onUploaded(file);
      }
    },
    attachmentUrl(file) {
      const url = new URL(file.ServerRelativeUrl, site);
      return url.origin === new URL(site).origin ? url.href : undefined;
    },
  };
}
